import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IBankModalContent, IBuilderModalContent, EModalType, IOffer } from '../../types';

import { openModal, setIsErrorGetOffersListing } from '../../redux';
import { IGetOffersV1Response } from '../../../common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/GetOffersV1Response';

import { useIsDesktop } from '../../hooks/useIsDesktop';
import { MobileListingContainer } from '../MobileListingContainer';
import { DesktopListingContainer } from '../DesktopListingContainer';
import { LoaderBlock } from '../../components/LoaderBlock';
import { SomethingWentWrong } from '../../components/SomethingWentWrong';
import { OfferEmpty } from '../../components/OfferEmpty';
import { mapOffersListForAnalytics } from '../../utils/';
import { mapOfferForAnalytics } from '../../utils/mapOffersListForAnalytics';
import { setOfferForAnalytic } from '../../redux/slices/analyticSlice';
import { useTracking } from '../../utils/tracking/useTracking';
import { selectSaveOffersError } from '../../selectors/calculator';

interface IListingContainerProps {
  data: IGetOffersV1Response | null;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
}

export const ListingContainer = (props: IListingContainerProps) => {
  const { data, isFetching, error, refetch } = props;
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const { trackClickRefresh, trackClickOnOffer, trackShowModal } = useTracking();
  const saveOffersError = useSelector(selectSaveOffersError);

  const handleClickRefresh = useCallback(() => {
    trackClickRefresh();
    dispatch(setIsErrorGetOffersListing(false));
    refetch();
  }, [dispatch, refetch, trackClickRefresh]);

  if ((isDesktop && isFetching) || (!data && !error)) {
    return <LoaderBlock />;
  } else if (error || !data || saveOffersError) {
    return <SomethingWentWrong onRefresh={handleClickRefresh} />;
  } else if (data.offers.length === 0) {
    return <OfferEmpty />;
  }

  const { offers, meta } = data;

  const offersForAnalytic = mapOffersListForAnalytics(offers);

  const handleClickOnCard = (offer: IOffer) => () => {
    const { offerName, offerDescription, iconPath, type } = offer;
    const baseContent = { offerName, offerDescription, iconPath };
    const offerPosition = offers.indexOf(offer) + 1;
    const offerForAnalytics = mapOfferForAnalytics(offer);
    trackClickOnOffer(offerPosition, offerForAnalytics);

    const modalContent =
      offer.type === EModalType.BANK
        ? { ...baseContent, ...(offer.modal as IBankModalContent) }
        : {
            ...baseContent,
            ...(offer.modal as IBuilderModalContent),
            rate: offer.rate,
            monthlyPayment: offer.monthlyPayment,
          };

    dispatch(setOfferForAnalytic({ offerForAnalytics }));
    dispatch(openModal({ content: modalContent, type }));
    trackShowModal(offerForAnalytics);
  };

  return isDesktop ? (
    <DesktopListingContainer
      offerList={offers}
      offerListForAnalytics={offersForAnalytic}
      onCardClick={handleClickOnCard}
    />
  ) : (
    <MobileListingContainer
      offerList={offers}
      offerListForAnalytics={offersForAnalytic}
      offerSummary={{
        minRate: Number(meta.minRate),
        minMonthlyPayment: Number(meta.minMonthlyPayment),
        totalOffersCount: meta.totalOffersCount,
        bankLogosPaths: meta.banksLogosPaths,
      }}
      onCardClick={handleClickOnCard}
      isLoading={isFetching}
    />
  );
};
